/**
 * Schema version - increment when data format changes.
 * If stored version doesn't match, data will be discarded.
 *
 * Version history:
 * - v7: Original format with DomainModel (classes + individuals + relationships)
 * - v8: TBox/ABox split - KnowledgeGraph (individuals + assertions), OntologySchema loaded from ontology
 */
export const DIAGRAM_SCHEMA_VERSION = 8;

/**
 * Storage keys for localStorage.
 * Diagram and KnowledgeGraph now use ID-based keys for multi-diagram support.
 */
export const STORAGE_KEYS = {
    /** Index of all diagram IDs (JSON array) */
    DIAGRAM_INDEX: 'semantical.diagrams',
    /** Individual diagram data: semantical.diagram.{id} */
    DIAGRAM_PREFIX: 'semantical.diagram.',
    /** Individual knowledge graph data: semantical.knowledgeGraph.{id} */
    KNOWLEDGE_GRAPH_PREFIX: 'semantical.knowledgeGraph.',
    /** Global app settings */
    APP_CONFIG: 'semantical.appConfig',
    /** Namespace color customizations (Map of namespace IRI to hex color) */
    NAMESPACE_COLORS: 'semantical.namespaceColors',
    /** Toolbox view mode preference ('hierarchical' or 'flat') */
    TOOLBOX_VIEW_MODE: 'semantical.toolboxViewMode',
    /** Toolbox group by ontology preference (boolean) */
    TOOLBOX_GROUP_BY_ONTOLOGY: 'semantical.toolboxGroupByOntology',
    /** URL-loaded domain URLs (JSON array of {url, domainId} objects) */
    URL_DOMAINS: 'semantical.urlDomains',
    /** Active bounded context ID */
    ACTIVE_CONTEXT_ID: 'semantical.activeContextId',
} as const;

/**
 * App configuration settings that persist across sessions.
 * Note: showStereotype, showEssentialProperties, showOptionalProperties,
 * inferenceEnabled, and editingMode are now stored per-diagram in the Diagram class.
 */
export interface AppConfig {
    showChat: boolean;
    darkMode?: boolean;
    browserWidth?: number;
}

/**
 * Versioned envelope for all persisted data
 */
export interface VersionedData<T> {
    version: number;
    timestamp: number;
    domainId?: string;  // Optional domain ID to validate data belongs to expected domain
    data: T;
}
